import { CoordinatorService } from './coordinator.service';
import { ScriptAttachment } from './script-attachment';

type INP = {
    id?: string;
    targetId: string;
    values?: INPV;
    ownerId?: string;
};

type INPV = {
    title?: string;
    description?: string;
    mimetype?: string;
    url?: string;
    size?: number;
    cachename?: string;
    filename?: string;
    [key: string]: string | number | undefined;
};

export class Attachment {
    id: string;
    targetId: string;
    private _values: INPV;
    private _editValues: INPV | undefined;
    private _ownerId: string;

    private _objectURL?: string;

    constructor(
        public coords: CoordinatorService,
        params: INP,
    ) {
        this.id = params.id || this.coords.uniqueId();
        this._ownerId = params.ownerId || this.coords.user$.currentId;
        this.targetId = params.targetId;
        this._values = Object.assign({}, params.values) || {};
    }

    clone() {
        return new Attachment(this.coords, {
            targetId: '_clone',
            ownerId: this._ownerId,
            values: this._values,
        });
    }

    get isOwned() {
        return this._ownerId === this.coords.user$.currentId;
    }

    get ownerId() {
        return this._ownerId;
    }

    get isDeletable() {
        return this.isOwned;
    }

    get user() {
        return this.coords.user$.get(this._ownerId);
    }
    async selectFile(preselectedFile: File | undefined = undefined) {
        const file = preselectedFile || (await this.coords.filesystem$.pickFiles());
        const resp = (await this.coords.worker$.manifest.call('save-attachment', {
            id: this.id,
            file: file,
        })) as any;

        const payload = resp?.payload;
        const cachename = payload?.fH?.name;

        if (!cachename) return;

        this._editValues = {};
        this._editValues.mimetype = this.coords.utils$.guessMimetype(file.name);
        this._editValues.size = file.size;
        this._editValues.filename = file.name;
        this._editValues.cachename = cachename;

        this.coords.display$.markForCheck();
    }

    get editValues() {
        if (!this._editValues) this._editValues = { ...this._values };
        return this._editValues;
    }

    get title() {
        return this._values.title || this.filename || this.url;
    }

    get url() {
        return this._values.url;
    }

    get mimetype() {
        if (!this._values.mimetype && this.url) {
            try {
                const url = new URL(this.url);
                return this.coords.utils$.guessMimetype(url);
            } catch (e) {}
        }
        return this._values.mimetype || 'application/octet-stream';
    }

    get filename() {
        return this._values.filename;
    }

    get description() {
        return this._values.description;
    }

    get imgSrc() {
        if (this._objectURL) return this._objectURL;
        if (this._values.cachename) {
            this.open();
            setTimeout(() => this.coords.display$.markForCheck(), 1000);
            return '';
        }
        return this.url;
    }

    revokeObjectURL() {
        if (!this._objectURL) return;
        URL.revokeObjectURL(this._objectURL);
        this._objectURL = undefined;
        this.coords.display$.markForCheck();
    }

    async open(): Promise<any> {
        const cache = this._values.cachename;

        if (cache) {
            if (!this._objectURL)
                try {
                    const r = await navigator.storage.getDirectory();
                    let dH = await r.getDirectoryHandle('_attachments');
                    if (!dH) return { error: 'CACHE not found' };
                    let fH: FileSystemFileHandle;
                    try {
                        fH = await dH.getFileHandle(cache);
                    } catch (e) {
                        if (this.coords.project$.current?.isCloud) {
                            if (
                                await this.coords.firebase$.storage.downloadAttachmentFile(
                                    this._remotePath,
                                )
                            )
                                fH = await dH.getFileHandle(cache);
                        } else return { error: 'CACHE not found' };
                    }
                    const f = await fH!.getFile();
                    // const txt = await f.text();
                    this._objectURL = URL.createObjectURL(
                        new Blob([f], {
                            type: this._values.mimetype,
                        }),
                    );
                    setTimeout(() => {
                        this.revokeObjectURL();
                    }, 12000000);
                    // window.open(objectURL, '_blank');
                } catch (e) {
                    // if (!this.coords.user$.cloudAttachmentManager) {
                    //     alert('FILE NOT AVAILABLE');
                    //     return;
                    // }
                    // const resp = await this.coords.user$.cloudAttachmentManager.download(
                    //     this._values.cachename!,
                    // );
                    // // resp && this.open();
                    // if (resp) {
                    //     return await this.open();
                    // }
                }
            return { objectURL: this._objectURL };
        }

        // // if (url) return this.coords.externalWeb$.open(url);
        // if (url) return { url };
        // return { error: 'No URL' };
    }

    get isImage() {
        return this.mimetype?.startsWith('image/');
    }

    get isPDF() {
        return this.mimetype?.startsWith('application/pdf');
    }

    delete() {
        this.revokeObjectURL();
        this.coords.attachment$.delete(this);
        this.coords.display$.markForCheck();
        this.coords.attachment$.markForManifestStore();
    }

    get deleteParams() {
        return {
            icon: 'delete',
            fun: () => this.delete(),
        };
    }

    cancelEdit() {
        this._editValues = undefined;
        this.coords.display$.closeDialog();
    }

    resetFileValues() {
        // reset all fields in editValues except description
        const description = this._editValues?.description;
        this._editValues = { description };
    }

    async informFileSaved(fH: FileSystemFileHandle) {
        // this.coords.user$.cloudAttachmentManager?.upload(fH)
    }

    get isSubmitDisabled() {
        return !this._editValues!.url && !this._editValues!.cachename;
    }

    private get _remotePath() {
        const cachename = this._values.cachename;
        const pId = this.coords.project$.currentId;
        const uId = this.coords.project$.current?.team?.owner.id;
        return `userdata/${uId}/projects/${pId}/Attachments/${cachename}`;
    }

    async cloudUpload() {
        const cachename = this._values.cachename;
        const r = await navigator.storage.getDirectory();
        try {
            if (!cachename) throw new Error('No attachment file to upload');
            let dH = await r.getDirectoryHandle('_attachments');
            const fH = await dH.getFileHandle(cachename);
            const rPth = this._remotePath;
            const f = await fH.getFile();
            const mimetype = this._values.mimetype;
            const resp = await this.coords.firebase$.storage.uploadFile(
                rPth,
                f,
                mimetype || 'application/octet-stream',
            );
            if (resp) {
                console.log('File uploaded successfully', resp);
                return true;
            } else {
                throw new Error('Failed to upload file');
            }
            console.log('FH', fH);
        } catch (e: any) {
            console.error('ATTACHMENT FILE not found');
            return false;
        }
    }

    async submit(params?: any) {
        if (params) this._editValues = params;
        this._values = { ...this._editValues };
        this._editValues = undefined;
        const cachename = this._values.cachename;
        if (cachename && this.coords.project$.inCloud) {
            if (!(await this.cloudUpload())) {
                alert('Failed to upload file to cloud');
                return;
            }
        }

        console.log(111111111, this._values);
        this.coords.attachment$.add(this);
        this.coords.display$.markForCheck();
        this.coords.attachment$.markForManifestStore();
    }

    save() {
        return {
            id: this.id,
            targetId: this.targetId,
            values: this._values,
        };
    }

    assign(targetId: string) {
        this.targetId = targetId;
        this.coords.attachment$.add(this);
    }

    get scriptClass() {
        return new ScriptAttachment(this.coords, this);
    }
}
